import React from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { renderStore } from "../../../../store/renderStore";
import {
  ClippingPolygonType,
  SymbolType,
  WallFaceSide,
  WallType,
  isDoubleDoorType,
  isSingleLeafDoorType,
  isVoidType,
  isWindowType,
} from "../../../../types/floorplannerTypes";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import HiddenOccluderEdges from "../../scene/HiddenOccluderEdges";
import SelectedMeshOutline from "../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../scene/usePointerUpSelection";
import {
  applyWallTextureUVs,
  applyWallEndpointHeights,
  buildWallExtrudeShapes,
  canUseSelectableWallFaceGeometry,
  createStraightWallOpeningGeometry,
  getWall3DHeight,
  getWallColor,
  getWallOpeningGeometryClippings,
  shouldUseIntegratedOpeningGeometry,
  restrictExtrudeGeometryToPart,
  WallEndpointHeights,
} from "./wallGeometry";
import Wall3DMaterial from "./Wall3DMaterial";
import {
  clipShapesToWallSide,
  resolveWallFaceAppearance,
} from "../../../../utils/wallFaceSides";
import { wallUsesDifferentSideColors } from "../../../../utils/fillSelection";

const WALL_FACE_SIDES: WallFaceSide[] = ["left", "right"];

/**
 * 3D wall meshes. Selection matches staging (whole wall).
 * When different-side colors are enabled, broad faces render as two FrontSide
 * meshes so left/right paints can differ; picking still selects the wall.
 */
const Wall3DMeshes: React.FC<{
  wall: WallType;
  heightProfile?: WallEndpointHeights;
  selected: boolean;
  cameraHidden: boolean;
  claimSelectionEvents?: boolean;
  onSelect: (wall: WallType, event: ThreeEvent<PointerEvent>) => void;
}> = observer(
  ({
    wall,
    heightProfile,
    selected,
    cameraHidden,
    claimSelectionEvents = false,
    onSelect,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { gl } = useThree();
    const selectionHandlers = usePointerUpSelection(wall, onSelect, {
      claimPointerEvents: claimSelectionEvents,
    });

    const wallShape = renderStore.innerWallShapesMap.get(wall.id)?.[0];
    const wallClippings = renderStore.wallClippingsMap.get(wall.id);
    const combinedWallClippings = getWallOpeningGeometryClippings(
      wall,
      floorplannerStore.wallsMap.values(),
      floorplannerStore.wallWidth,
      wallClippings,
    );
    const wallClippingSignature = getWallClippingSignature(
      combinedWallClippings,
    );
    const openingGeometrySignature = getOpeningGeometrySignature(
      wall,
      floorplannerStore.symbolsMap,
    );
    const openingSymbols = React.useMemo(
      () =>
        (wall.symbolAttachments ?? [])
          .map((attachment) =>
            floorplannerStore.symbolsMap.get(attachment.symbolId),
          )
          .filter(isOpeningSymbol),
      [floorplannerStore.symbolsMap, openingGeometrySignature, wall],
    );
    const height = getWall3DHeight(wall);
    const resolvedHeightProfile = heightProfile ?? {
      start: height,
      end: height,
    };
    const extrusionHeight = Math.max(
      height,
      resolvedHeightProfile.start,
      resolvedHeightProfile.end,
    );

    const useDifferentSideColors =
      wall.lineForm !== "arc" && wallUsesDifferentSideColors(wall);
    const useSplitFaceGeometry =
      useDifferentSideColors &&
      canUseSelectableWallFaceGeometry(
        wall,
        wallShape,
        combinedWallClippings,
      );
    const useIntegratedGeometry = shouldUseIntegratedOpeningGeometry(
      wall,
      wallShape,
      combinedWallClippings,
      openingSymbols,
      useSplitFaceGeometry,
    );

    const buildGeometry = React.useCallback(
      (options?: {
        broadFaces?: "left" | "right" | "both" | "none";
        includeStructure?:
          | boolean
          | {
              horizontal?: boolean;
              endCaps?: boolean | "start" | "end" | "both";
              openings?: boolean;
            };
      }): THREE.BufferGeometry | null => {
        if (!useIntegratedGeometry) return null;
        const geometry = createStraightWallOpeningGeometry(
          wall,
          openingSymbols,
          {
            wallShape,
            wallWidth: wall.wallWidth ?? floorplannerStore.wallWidth,
            wallHeight: extrusionHeight,
            defaultDoorFrameWidth: floorplannerStore.doorFrameWidth,
            defaultWindowFrameWidth: floorplannerStore.windowFrameWidth,
            clippingPolygons: combinedWallClippings,
            ...(useSplitFaceGeometry
              ? {
                  broadFaces: options?.broadFaces ?? "both",
                  includeStructure: options?.includeStructure ?? true,
                }
              : {}),
          },
        );
        if (geometry) {
          applyWallEndpointHeights(
            geometry,
            wall,
            resolvedHeightProfile,
            extrusionHeight,
          );
          applyWallTextureUVs(geometry, wall);
        }
        return geometry;
      },
      [
        combinedWallClippings,
        extrusionHeight,
        floorplannerStore.doorFrameWidth,
        floorplannerStore.wallWidth,
        floorplannerStore.windowFrameWidth,
        // Attachment slides (arrow keys) change this signature → rebuild holes.
        openingGeometrySignature,
        openingSymbols,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        useIntegratedGeometry,
        useSplitFaceGeometry,
        wall,
        wallShape,
        wallClippingSignature,
      ],
    );

    const useHalfBandExtrusions =
      useDifferentSideColors &&
      !useSplitFaceGeometry &&
      !cameraHidden &&
      !useIntegratedGeometry;

    const sideFaceMeshes = React.useMemo(() => {
      if (cameraHidden || !useSplitFaceGeometry) return null;
      return WALL_FACE_SIDES.map((side) => ({
        side,
        geometry: buildGeometry({
          broadFaces: side,
          includeStructure: false,
        }),
      }));
    }, [buildGeometry, cameraHidden, useSplitFaceGeometry]);

    const halfBandShapes = React.useMemo(() => {
      if (!useHalfBandExtrusions) return null;
      const wallW = wall.wallWidth ?? floorplannerStore.wallWidth;
      // Start from composed extrude shapes (miters / clips), then split by side.
      // Keep every clipped polygon — walls with windows/doors often yield several
      // segments per side; dropping all but [0] makes mid-wall sections vanish.
      const composed = buildWallExtrudeShapes(
        wallShape,
        combinedWallClippings,
        wall.lineWeight ?? floorplannerStore.wallLineWeight,
        wallW,
      );
      return {
        composed,
        sides: WALL_FACE_SIDES.flatMap((side) => {
          const sideShapes = clipShapesToWallSide(composed, wall, side, wallW);
          return sideShapes.map((shape, index) => ({ side, shape, index }));
        }),
      };
    }, [
      combinedWallClippings,
      floorplannerStore.wallLineWeight,
      floorplannerStore.wallWidth,
      useHalfBandExtrusions,
      wall,
      wall.lineWeight,
      wallShape,
      wallClippingSignature,
    ]);

    const structureGeometry = React.useMemo(() => {
      if (cameraHidden || !useSplitFaceGeometry) return null;
      return buildGeometry({
        broadFaces: "none",
        includeStructure: {
          horizontal: true,
          endCaps: true,
          openings: false,
        },
      });
    }, [buildGeometry, cameraHidden, useSplitFaceGeometry]);

    const openingStructureGeometry = React.useMemo(() => {
      if (cameraHidden || !useSplitFaceGeometry) return null;
      return buildGeometry({
        broadFaces: "none",
        includeStructure: {
          horizontal: false,
          endCaps: false,
          openings: true,
        },
      });
    }, [buildGeometry, cameraHidden, useSplitFaceGeometry]);

    const straightGeometry = React.useMemo(() => {
      if (cameraHidden || useSplitFaceGeometry || !useIntegratedGeometry) {
        return null;
      }
      return buildGeometry();
    }, [
      buildGeometry,
      cameraHidden,
      useIntegratedGeometry,
      useSplitFaceGeometry,
    ]);

    const fallbackShapes = React.useMemo(
      () =>
        cameraHidden || useIntegratedGeometry || useHalfBandExtrusions
          ? []
          : buildWallExtrudeShapes(
              wallShape,
              combinedWallClippings,
              wall.lineWeight ?? floorplannerStore.wallLineWeight,
              wall.wallWidth ?? floorplannerStore.wallWidth,
            ),
      [
        cameraHidden,
        combinedWallClippings,
        floorplannerStore.wallLineWeight,
        floorplannerStore.wallWidth,
        useHalfBandExtrusions,
        useIntegratedGeometry,
        wall.lineWeight,
        wall.wallWidth,
        wallShape,
        wallClippingSignature,
      ],
    );
    const hiddenEdgeShapes = React.useMemo(
      () =>
        cameraHidden
          ? buildWallExtrudeShapes(
              wallShape,
              undefined,
              wall.lineWeight ?? floorplannerStore.wallLineWeight,
              wall.wallWidth ?? floorplannerStore.wallWidth,
            )
          : [],
      [
        cameraHidden,
        floorplannerStore.wallLineWeight,
        floorplannerStore.wallWidth,
        wall.lineWeight,
        wall.wallWidth,
        wallShape,
      ],
    );
    const configureWallTextureUVs = React.useCallback(
      (geometry: THREE.ExtrudeGeometry) => {
        applyWallEndpointHeights(
          geometry,
          wall,
          resolvedHeightProfile,
          extrusionHeight,
        );
        applyWallTextureUVs(geometry, wall);
      },
      [
        extrusionHeight,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        wall,
      ],
    );
    // Half-band pieces draw only their vertical faces; one shared cap mesh per
    // composed shape draws the top/bottom so the wall top is not split by side.
    const configureHalfBandSides = React.useCallback(
      (geometry: THREE.ExtrudeGeometry) => {
        configureWallTextureUVs(geometry);
        restrictExtrudeGeometryToPart(geometry, "sides");
      },
      [configureWallTextureUVs],
    );
    const configureHalfBandCaps = React.useCallback(
      (geometry: THREE.ExtrudeGeometry) => {
        configureWallTextureUVs(geometry);
        restrictExtrudeGeometryToPart(geometry, "caps");
      },
      [configureWallTextureUVs],
    );
    const configureHiddenWallEdges = React.useCallback(
      (geometry: THREE.BufferGeometry) => {
        applyWallEndpointHeights(
          geometry,
          wall,
          resolvedHeightProfile,
          extrusionHeight,
        );
      },
      [
        extrusionHeight,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        wall,
      ],
    );
    React.useEffect(
      () => () => {
        sideFaceMeshes?.forEach((entry) => entry.geometry?.dispose());
        structureGeometry?.dispose();
        openingStructureGeometry?.dispose();
        straightGeometry?.dispose();
      },
      [
        openingStructureGeometry,
        sideFaceMeshes,
        straightGeometry,
        structureGeometry,
      ],
    );

    const defaultColor = getWallColor(wall);

    const cursorHandlers = {
      onPointerOver: () => {
        gl.domElement.style.cursor = "pointer";
      },
      onPointerOut: () => {
        gl.domElement.style.cursor = "default";
      },
    };

    if (cameraHidden) {
      return (
        <>
          {hiddenEdgeShapes.map((shape, index) => (
            <HiddenOccluderEdges
              key={`${wall.id}-edge-${index}`}
              shape={shape}
              mode="vertical"
              height={extrusionHeight}
              color={defaultColor}
              configureGeometry={configureHiddenWallEdges}
              userData={{
                floorplannerType: "wall-edge",
                floorplannerId: wall.id,
              }}
            />
          ))}
        </>
      );
    }

    if (sideFaceMeshes) {
      return (
        <>
          {sideFaceMeshes.map(({ side, geometry }) => {
            if (!geometry) return null;
            // MobX: observe face paint contents so recolors re-render.
            const faceMaterialsSignature = `${wall.threeD?.faceMaterials?.left?.fillColor ?? ""}:${wall.threeD?.faceMaterials?.right?.fillColor ?? ""}:${wall.threeD?.faceMaterials?.left?.surfaceRef?.id ?? ""}:${wall.threeD?.faceMaterials?.right?.surfaceRef?.id ?? ""}`;
            void faceMaterialsSignature;
            const appearance = resolveWallFaceAppearance(wall, side);
            const color =
              appearance.fillColorIsOverride &&
              appearance.fillColor !== undefined
                ? new THREE.Color(appearance.fillColor)
                : appearance.surfaceRef?.color !== undefined
                  ? new THREE.Color(appearance.surfaceRef.color)
                  : appearance.fillColor !== undefined
                    ? new THREE.Color(appearance.fillColor)
                    : defaultColor;
            return (
              <mesh
                key={`${wall.id}-${side}`}
                geometry={geometry}
                castShadow
                receiveShadow
                userData={{
                  floorplannerType: "wall",
                  floorplannerId: wall.id,
                }}
                {...selectionHandlers}
                {...cursorHandlers}
              >
                <Wall3DMaterial
                  wall={wall}
                  color={color}
                  surfaceRef={appearance.surfaceRef}
                  side={THREE.FrontSide}
                />
                <SelectedMeshOutline
                  visible={selected}
                  side={THREE.FrontSide}
                />
              </mesh>
            );
          })}
          {structureGeometry && (
            <mesh
              geometry={structureGeometry}
              castShadow
              receiveShadow
              userData={{
                floorplannerType: "wall",
                floorplannerId: wall.id,
              }}
              {...selectionHandlers}
              {...cursorHandlers}
            >
              <Wall3DMaterial
                wall={wall}
                color={defaultColor}
                side={THREE.DoubleSide}
              />
              <SelectedMeshOutline visible={selected} />
            </mesh>
          )}
          {openingStructureGeometry && (
            <mesh
              geometry={openingStructureGeometry}
              castShadow
              receiveShadow
              userData={{
                floorplannerType: "wall",
                floorplannerId: wall.id,
              }}
              {...selectionHandlers}
              {...cursorHandlers}
            >
              <Wall3DMaterial
                wall={wall}
                color={defaultColor}
                side={THREE.DoubleSide}
              />
              <SelectedMeshOutline visible={selected} />
            </mesh>
          )}
        </>
      );
    }

    if (halfBandShapes) {
      return (
        <>
          {halfBandShapes.composed.map((shape, index) => (
            <ExtrudedShapeMesh
              key={`${wall.id}-half-cap-${index}`}
              shape={shape}
              depth={extrusionHeight}
              selected={selected}
              receiveShadow
              configureGeometry={configureHalfBandCaps}
              {...selectionHandlers}
              userData={{
                floorplannerType: "wall",
                floorplannerId: wall.id,
              }}
            >
              <Wall3DMaterial
                wall={wall}
                color={defaultColor}
                side={THREE.DoubleSide}
              />
            </ExtrudedShapeMesh>
          ))}
          {halfBandShapes.sides.map(({ side, shape, index }) => {
            const faceMaterialsSignature = `${wall.threeD?.faceMaterials?.left?.fillColor ?? ""}:${wall.threeD?.faceMaterials?.right?.fillColor ?? ""}`;
            void faceMaterialsSignature;
            const appearance = resolveWallFaceAppearance(wall, side);
            const color =
              appearance.fillColorIsOverride &&
              appearance.fillColor !== undefined
                ? new THREE.Color(appearance.fillColor)
                : appearance.surfaceRef?.color !== undefined
                  ? new THREE.Color(appearance.surfaceRef.color)
                  : appearance.fillColor !== undefined
                    ? new THREE.Color(appearance.fillColor)
                    : defaultColor;
            return (
              <ExtrudedShapeMesh
                key={`${wall.id}-half-${side}-${index}`}
                shape={shape}
                depth={extrusionHeight}
                selected={selected}
                receiveShadow
                configureGeometry={configureHalfBandSides}
                {...selectionHandlers}
                userData={{
                  floorplannerType: "wall",
                  floorplannerId: wall.id,
                }}
              >
                <Wall3DMaterial
                  wall={wall}
                  color={color}
                  surfaceRef={appearance.surfaceRef}
                  side={THREE.DoubleSide}
                />
              </ExtrudedShapeMesh>
            );
          })}
        </>
      );
    }

    if (straightGeometry) {
      return (
        <mesh
          geometry={straightGeometry}
          castShadow
          receiveShadow
          userData={{ floorplannerType: "wall", floorplannerId: wall.id }}
          {...selectionHandlers}
          {...cursorHandlers}
        >
          <Wall3DMaterial
            wall={wall}
            color={defaultColor}
            side={THREE.DoubleSide}
          />
          <SelectedMeshOutline visible={selected} />
        </mesh>
      );
    }

    return (
      <>
        {fallbackShapes.map((shape, index) => (
          <ExtrudedShapeMesh
            key={`${wall.id}-${index}`}
            shape={shape}
            depth={extrusionHeight}
            selected={selected}
            receiveShadow
            configureGeometry={configureWallTextureUVs}
            {...selectionHandlers}
            userData={{ floorplannerType: "wall", floorplannerId: wall.id }}
          >
            <Wall3DMaterial
              wall={wall}
              color={defaultColor}
              side={THREE.DoubleSide}
            />
          </ExtrudedShapeMesh>
        ))}
      </>
    );
  },
);

function isOpeningSymbol(symbol: SymbolType | undefined): symbol is SymbolType {
  return (
    !!symbol &&
    (isSingleLeafDoorType(symbol) ||
      isDoubleDoorType(symbol) ||
      isWindowType(symbol) ||
      isVoidType(symbol))
  );
}

function getOpeningGeometrySignature(
  wall: WallType,
  symbolsMap: Map<string, SymbolType>,
): string {
  return (wall.symbolAttachments ?? [])
    .map((attachment) => {
      const symbol = symbolsMap.get(attachment.symbolId);
      return [
        attachment.symbolId,
        attachment.positionFromStart,
        symbol?.type ?? "",
        symbol && isSingleLeafDoorType(symbol) ? symbol.doorWidth : "",
        symbol && isDoubleDoorType(symbol) ? symbol.doubleDoorWidth : "",
        symbol && isWindowType(symbol) ? symbol.windowLength : "",
        symbol && isVoidType(symbol) ? symbol.voidLength : "",
        symbol && (isSingleLeafDoorType(symbol) || isDoubleDoorType(symbol))
          ? (symbol.doorFrameWidth ?? "")
          : "",
        symbol && isWindowType(symbol) ? (symbol.windowFrameWidth ?? "") : "",
        symbol?.threeD?.height ?? "",
        symbol?.threeD?.baseElevation ?? "",
        symbol?.threeD?.topBulge ?? "",
      ].join(":");
    })
    .join("|");
}

function getWallClippingSignature(
  clippings: ClippingPolygonType[] | undefined,
): string {
  return (clippings ?? [])
    .map((clipping) =>
      [
        clipping.belongsTo,
        clipping.objectType,
        clipping.polygon.map((point) => `${point.x},${point.y}`).join(";"),
      ].join(":"),
    )
    .join("|");
}

export default Wall3DMeshes;
